import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;

export default function ProfileGate({ children }) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const location = useLocation();
  const [profileStatus, setProfileStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkProfile = async () => {
      if (!isLoaded || !isSignedIn) {
        setLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/profile/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success) {
          setProfileStatus(data.data);
        }
      } catch (error) {
        console.error('Error checking profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkProfile();
  }, [isLoaded, isSignedIn, getToken]);

  // Still loading auth
  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Not signed in - redirect immediately to sign-in
  // This must be checked before profileStatus to avoid infinite spinner after logout
  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Profile status not loaded yet - wait before making any redirect decision
  // This prevents the login flash during the race condition between auth loading
  // and the profile API check completing (only applies when user IS signed in)
  if (profileStatus === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // User doesn't exist in our database yet - send back to sign-up
  if (!profileStatus.exists || !profileStatus.profileCompleted) {
    return <Navigate to="/sign-up" replace />;
  }

  // All good, render children
  return children;
}
